import { differenceInMonths } from 'date-fns';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { getPropertiesList } from 'entities/Property/model/selectors/PropertySelectors';
import { useSpendSummaryReportVisible } from 'shared/lib/hooks/useSpendSummaryReportVisible';
import { parseUtcIsoToDate } from 'shared/lib/reportDatePresets';
import { useSpendSummaryDrillInData } from 'widgets/SpendSummaryReport/model/hooks/useSpendSummaryDrillInData';

import type {
    SpendCategoryRowDto,
    SpendInsightsSummaryDto,
    SpendProductsResponseDto,
    SpendSupplierRowDto,
} from 'entities/ReportSpendSummary';
import type { SpendDrillInLocationState } from 'widgets/SpendSummaryReport/model/types/spendDrillIn';

interface UseReportsPageSpendSummaryPropertyResult {
    isSpendSummaryVisible: boolean | undefined;
    propertyId: string | undefined;
    propertyName: string;
    loading: boolean;
    summary: SpendInsightsSummaryDto | null;
    byCategory: SpendCategoryRowDto[];
    bySupplier: SpendSupplierRowDto[];
    products: SpendProductsResponseDto | null;
    totalSpend: number;
    spendPerUnit: number | null;
    spendPerUnitPerMonth: number | null;
    orderCount: number;
    spendPerOrder: number | null;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
    locationState: SpendDrillInLocationState;
    showCategorySection: boolean;
    showSupplierSection: boolean;
}

export const useReportsPageSpendSummaryProperty =
    (): UseReportsPageSpendSummaryPropertyResult => {
        const { propertyId } = useParams<{ propertyId: string }>();
        const location = useLocation();
        const isSpendSummaryVisible = useSpendSummaryReportVisible();
        const properties = useSelector(getPropertiesList);
        const locationState =
            (location.state as SpendDrillInLocationState | null) ?? {};

        const {
            loading,
            summary,
            byCategory,
            bySupplier,
            products,
            totalSpend,
            orderCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
            showCategorySection,
            showSupplierSection,
        } = useSpendSummaryDrillInData({
            dimension: 'property',
            id: propertyId,
            scope: locationState.scope,
        });

        const propertyName = useMemo(() => {
            if (locationState.propertyName) {
                return locationState.propertyName;
            }

            const fromSummary = summary?.byProperties?.find(
                (item) => item.propertyId === propertyId,
            )?.propertyName;
            if (fromSummary) {
                return fromSummary;
            }

            const fromPropertiesList = properties?.find(
                (item) => item.id === propertyId,
            );
            const fromPropertiesListName =
                fromPropertiesList?.propertyNameMarketing ||
                fromPropertiesList?.propertyNameLegal;
            if (fromPropertiesListName) {
                return fromPropertiesListName;
            }

            return propertyId ?? '';
        }, [propertyId, locationState.propertyName, properties, summary]);

        const unitCount = useMemo(() => {
            if (!propertyId) {
                return null;
            }

            const property = properties?.find((item) => item.id === propertyId);
            if (!property || typeof property.unitCount !== 'number') {
                return null;
            }

            return property.unitCount;
        }, [properties, propertyId]);

        const monthsInRange =
            dateFrom && dateTo
                ? Math.max(
                      1,
                      differenceInMonths(
                          parseUtcIsoToDate(dateTo),
                          parseUtcIsoToDate(dateFrom),
                      ) + 1,
                  )
                : 1;

        const spendPerUnit =
            unitCount != null && unitCount > 0 ? totalSpend / unitCount : null;
        const spendPerUnitPerMonth =
            unitCount != null && unitCount > 0 && monthsInRange > 0
                ? totalSpend / unitCount / monthsInRange
                : null;
        const spendPerOrder = orderCount > 0 ? totalSpend / orderCount : null;

        return {
            isSpendSummaryVisible,
            propertyId,
            propertyName,
            loading,
            summary,
            byCategory,
            bySupplier,
            products,
            totalSpend,
            spendPerUnit,
            spendPerUnitPerMonth,
            orderCount,
            spendPerOrder,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
            locationState,
            showCategorySection,
            showSupplierSection,
        };
    };
